import type { SalesOrder, SalesOrderListItem } from '@/lib/services/sales-orders/sales-orders.types'
import type { AdminRouterContext } from '@/lib/router-context'
import { RecordHistoryPage } from '@/components/audit-log/RecordHistoryPage'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { auditLogQueries } from '@/lib/services/audit-log/audit-log.query'
import { salesOrderSchema } from '@/lib/services/sales-orders/sales-order.schema'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { createFileRoute, notFound } from '@tanstack/react-router'

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/$salesOrderId_/history')({
  component: SalesOrderHistoryPage,
  params: {
    parse: (params) => salesOrderSchema.pick({ salesOrderId: true }).parse(params),
  },
  head: ({ params }) => adminTitleHead('History', `Order #${params?.salesOrderId}`, 'Orders'),
  loader: loadSalesOrderHistory,
})

export async function loadSalesOrderHistory({
  context,
  params,
}: {
  context: AdminRouterContext
  params: { orgId: number | string; salesOrderId: number | string }
}): Promise<{ salesOrder: SalesOrder | SalesOrderListItem }> {
  if (!context.ability.can('view', 'audit-logs') || !context.ability.can('view', 'sales-orders')) {
    throw notFound()
  }

  const [salesOrder] = await Promise.all([
    context.queryClient.ensureQueryData(
      salesOrderQueries.show({
        organizationId: params.orgId,
        salesOrderId: params.salesOrderId,
      })
    ),
    context.queryClient.ensureInfiniteQueryData(
      auditLogQueries.record({
        organizationId: params.orgId,
        subject: 'sales-orders',
        recordId: params.salesOrderId,
      })
    ),
  ])

  return { salesOrder }
}

export function SalesOrderHistoryPage() {
  const { orgId, salesOrderId } = Route.useParams()
  const { salesOrder } = Route.useLoaderData()
  const path = usePathHelper()

  return (
    <RecordHistoryPage
      backLabel="order"
      backLink={path.salesOrders.show(salesOrderId).link}
      description="Review important changes to this order's details, payments, and fulfillment."
      organizationId={salesOrder.organization_id ?? orgId}
      recordId={salesOrderId}
      recordLabel={`Order #${salesOrder.id ?? salesOrderId}`}
      subject="sales-orders"
    />
  )
}
